import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import BaseTable from "@/components_v2/Tables/BaseTable";
import type { KnowledgeCheckRow } from "@/redux/certifications/types";
import AnalyticsCard from "@/features/certifications/analytics/components/AnalyticsCard";
import {
  CARD_SIZES,
  PASS_RATE_LEVEL_NAMES,
} from "@/features/certifications/analytics/constants";
import {
  getPassRateLevel,
  getWeekByModule,
  toPercent,
} from "@/features/certifications/analytics/utils";
import { KnowledgeCheckCardProps } from "@/features/certifications/analytics/types";

import "../styles.css";

const KnowledgeCheckTableCard = ({
  certification,
  data,
  isLoading,
  isError,
}: KnowledgeCheckCardProps) => {
  const { t } = useTranslation();
  const prefix = "certification.admin.analytics.knowledgeChecks";

  const columns = useMemo(() => {
    const weekByModule = getWeekByModule(certification);
    const rate = (value: number | null) =>
      value === null
        ? t("charts.noValue")
        : t("charts.percent", { percent: toPercent(value) });

    return [
      {
        key: "check",
        title: t(`${prefix}.columns.check`),
        render: (_: unknown, row: KnowledgeCheckRow) => (
          <span className="flex flex-col">
            <span className="body-s-medium">{row.title}</span>

            <span className="analytics-muted body-xs-regular">
              {t(`${prefix}.moduleLabel`, {
                position: weekByModule.get(row.module.id) ?? "?",
                title: row.module.title,
              })}
            </span>
          </span>
        ),
      },
      {
        key: "attempted",
        title: t(`${prefix}.columns.attempted`),
        dataIndex: "attempted",
        align: "right" as const,
      },
      {
        key: "passed",
        title: t(`${prefix}.columns.passed`),
        dataIndex: "passed",
        align: "right" as const,
      },
      {
        key: "passRate",
        title: t(`${prefix}.columns.passRate`),
        align: "right" as const,
        render: (_: unknown, row: KnowledgeCheckRow) => {
          if (row.passRate === null) return rate(null);
          const level = getPassRateLevel(row.passRate);
          return (
            <span
              className={
                level.name === PASS_RATE_LEVEL_NAMES.LOW
                  ? "analytics-alert body-s-medium"
                  : ""
              }
            >
              {level.name === PASS_RATE_LEVEL_NAMES.LOW
                ? t(`${prefix}.lowRate`, { rate: rate(row.passRate) })
                : rate(row.passRate)}
            </span>
          );
        },
      },
      {
        key: "firstTry",
        title: t(`${prefix}.columns.firstTry`),
        align: "right" as const,
        render: (_: unknown, row: KnowledgeCheckRow) =>
          t(`${prefix}.firstTry`, {
            rate: rate(row.firstTryPassRate),
            count: row.passedFirstTry,
          }),
      },
      {
        key: "averageAttempts",
        title: t(`${prefix}.columns.averageAttempts`),
        align: "right" as const,
        render: (_: unknown, row: KnowledgeCheckRow) =>
          row.averageAttemptsToPass ?? t("charts.noValue"),
      },
      {
        key: "pendingReview",
        title: t(`${prefix}.columns.pendingReview`),
        dataIndex: "pendingReview",
        align: "right" as const,
      },
    ];
  }, [certification, t]);

  return (
    <AnalyticsCard
      title={t(`${prefix}.tableTitle`)}
      info={t(`${prefix}.info`)}
      isLoading={isLoading}
      isError={isError}
      size={CARD_SIZES.FULL}
    >
      <BaseTable<KnowledgeCheckRow>
        dataSource={data?.checks ?? []}
        columns={columns}
        rowKey="contentItem"
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: t(`${prefix}.empty`) }}
      />
    </AnalyticsCard>
  );
};

export default KnowledgeCheckTableCard;
